import { useCallback, useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import { collection, getDocs, query, where } from "firebase/firestore";
import {
  Permissions,
  TeamRole,
  TeamsStatus,
  Workspace,
} from "ts-interfaces";
import { allFirmIdsForUser, deriveTeamRole, isAdminRole } from "../team";
import { firebaseHelper } from "../firebase";
import { workspacesEnabled } from "./featureFlag";

const STORAGE_KEY = (uid: string) => `mattoboard.workspace.active.${uid}`;
const PERSONAL_ID = (uid: string) => `personal:${uid}`;
const TEAM_ID = (firmId: string) => `team:${firmId}`;

const readStored = (uid: string | null | undefined): string | null => {
  if (!uid || typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY(uid));
  } catch {
    return null;
  }
};

const writeStored = (uid: string | null | undefined, id: string | null): void => {
  if (!uid || typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY(uid), id);
    else window.localStorage.removeItem(STORAGE_KEY(uid));
  } catch {
    /* swallow quota / privacy-mode errors */
  }
};

const buildWorkspaces = (
  uid: string | null | undefined,
  permissions: Permissions | null | undefined,
): Workspace[] => {
  if (!uid) return [];
  const list: Workspace[] = [
    { id: PERSONAL_ID(uid), kind: "personal", uid, label: "Personal" },
  ];
  if (!permissions) return list;
  for (const firmId of allFirmIdsForUser(permissions)) {
    const role = deriveTeamRole(permissions, firmId);
    if (!role) continue;
    list.push({ id: TEAM_ID(firmId), kind: "team", firmId, role });
  }
  return list;
};

/**
 * Singleton store holding the active workspace id per-uid.
 *
 * useWorkspaceContext previously kept this in `useState`, but that gave each
 * component its own isolated copy — switcher updates didn't propagate to
 * the credit display, etc. Centralizing in a zustand store means every
 * caller (switcher, balance display, brief pre-flight, ...) reads the same
 * value and re-renders on switch.
 */
interface WorkspaceStoreState {
  activeIdByUid: Record<string, string>;
  /**
   * Bumped to force every `useWorkspaceContext` consumer to re-run its
   * active-firms fetch. The cached set isn't reactive to a `teams.status`
   * change (e.g. a member reactivating themselves), so a mutation that should
   * change "am I an active spender" pairs `clearActiveFirmsCache` + a bump
   * here via `refreshActiveFirms`.
   */
  activeFirmsVersion: number;
  /** Set the active workspace id for a uid. */
  setActive: (uid: string, id: string) => void;
  /** Seed an active id from localStorage. No-op if a value already exists. */
  hydrate: (uid: string, id: string) => void;
  /** Force a re-resolution of the active-firms set across all consumers. */
  bumpActiveFirmsVersion: () => void;
  /** Clear all state — for tests and on logout. */
  resetStore: () => void;
}

export const useWorkspaceStore = create<WorkspaceStoreState>((set) => ({
  activeIdByUid: {},
  activeFirmsVersion: 0,
  setActive: (uid, id) =>
    set((s) => ({ activeIdByUid: { ...s.activeIdByUid, [uid]: id } })),
  hydrate: (uid, id) =>
    set((s) => {
      if (s.activeIdByUid[uid] !== undefined) return s;
      return { activeIdByUid: { ...s.activeIdByUid, [uid]: id } };
    }),
  bumpActiveFirmsVersion: () =>
    set((s) => ({ activeFirmsVersion: s.activeFirmsVersion + 1 })),
  resetStore: () => set({ activeIdByUid: {}, activeFirmsVersion: 0 }),
}));

export interface WorkspaceContext {
  /** The active workspace. Defaults to Personal; never null when uid is set. */
  active: Workspace;
  /** All workspaces the user can switch into. Personal is always first.
   *  Inactive team memberships are filtered OUT — see inactiveTeamIds. */
  workspaces: Workspace[];
  /** Firm ids the user has permissions for but cannot act in right now
   *  (their `teams` row status is `inactive`). UI can surface these as
   *  disabled rows with a "contact your admin" hint. Empty until the
   *  status fetch resolves. */
  inactiveTeamIds: Set<string>;
  /** The inactive teams among them the user administers: they may reactivate
   *  their own membership (the one write the Teams sharing rules leave them). */
  inactiveAdminTeamIds: Set<string>;
  /** Switch to a workspace by id. No-op for unknown / inactive ids. */
  switchWorkspace: (id: string) => void;
  isPersonal: boolean;
  isTeam: boolean;
  /** Role within the active workspace, or null when Personal. */
  teamRole: TeamRole | null;
  /** True when the active workspace is a team AND the user is an admin in it. */
  isAdmin: boolean;
  /**
   * True when the user is an ACTIVE (non-inactive) roster member of the active
   * team workspace — i.e. eligible to spend its credits. Always true for
   * Personal and while the active-firms status is still resolving (so we don't
   * flash-hide). Distinct from `isAdmin`/`teamRole`: an inactive
   * designer_and_admin keeps team-page access but is NOT an active spender.
   */
  isActiveMemberOfActiveWorkspace: boolean;
  /** True once the teams' active/inactive status has loaded, so `workspaces`
   *  and `inactiveTeamIds` are final. True when workspaces are gated. */
  teamStatusResolved: boolean;
  /** Teams whose status couldn't be read. They count as inactive (fail-closed),
   *  so this is how to tell "unknown" from "made inactive". */
  teamStatusFailedIds: Set<string>;
  /** True while uid is still loading; downstream hooks should bail. */
  loading: boolean;
}

const PLACEHOLDER_LOADING_WORKSPACE: Workspace = {
  id: "personal:__loading__",
  kind: "personal",
  uid: "",
  label: "Personal",
};

// Stable references for the workspaces-disabled (production) context so the
// short-circuit return doesn't allocate a fresh Set/function each render.
const EMPTY_FIRM_IDS: Set<string> = new Set();
const NOOP_SWITCH = (_id: string): void => {};

/**
 * Per-uid cache of the set of firm ids where the user is ACTIVE (their
 * `teams` row exists and is `active`, or predates the status field). Mirrors the backend's
 * isUserMemberOfTeam check so the frontend doesn't expose teams the
 * server will reject with a 403.
 *
 * The cached value is tagged with the `teamFirmIds` key it was resolved for.
 * A consumer treats the cache as valid ONLY when that key matches the user's
 * current set of permission firms — so when permissions gain a firm (e.g. a
 * team was just created), the prior set is recognized as STALE rather than
 * silently used to filter the new firm out (which would bounce the active
 * workspace back to Personal). A mismatched (or absent) value is treated as
 * "still resolving": callers show all permission firms until the fetch lands.
 */
interface ActiveFirmsEntry {
  /** The teamFirmIds.join(",") this set was resolved for. */
  key: string;
  ids: Set<string>;
  /** Firms whose `teams` query failed; `ids` leaves them out (fail-closed). */
  failedIds: Set<string>;
}
const activeFirmsCache = new Map<string, ActiveFirmsEntry>();
// Keyed by `${uid}::${teamFirmIdsKey}` so a fetch in flight for one firm set
// is never reused to satisfy a different firm set.
const activeFirmsInFlight = new Map<string, Promise<ActiveFirms>>();

// Per-(uid, firmSet) cache of the ACTIVE subset of a specific list of firms,
// backing useActiveFirmIds (and, through it, useIsActiveRosterMember). Separate
// from activeFirmsCache (which is keyed to the user's FULL firm set) so a
// capability/credit lookup for a subset doesn't invalidate — or get invalidated
// by — the switcher's set. Cleared alongside activeFirmsCache in
// clearActiveFirmsCache / refreshActiveFirms.
const activeFirmSetCache = new Map<string, Set<string>>();
const activeFirmSetInFlight = new Map<string, Promise<Set<string>>>();
const activeFirmSetKey = (uid: string, firmIdsKey: string): string =>
  `${uid}::${firmIdsKey}`;

interface ActiveFirms {
  ids: Set<string>;
  failedIds: Set<string>;
}

async function fetchActiveFirms(
  uid: string,
  firmIds: string[],
): Promise<ActiveFirms> {
  const db = (firebaseHelper as any)?.db ?? null;
  if (!db || firmIds.length === 0) {
    return { ids: new Set(firmIds), failedIds: new Set() };
  }

  const active = new Set<string>();
  const failedIds = new Set<string>();
  // Firestore `in` clause supports up to 30 values per query.
  for (let i = 0; i < firmIds.length; i += 30) {
    const chunk = firmIds.slice(i, i + 30);
    try {
      const q = query(
        collection(db, "teams"),
        where("uid", "==", uid),
        where("designFirmID", "in", chunk),
      );
      const snap = await getDocs(q);
      snap.forEach((doc) => {
        const data = doc.data() as { designFirmID?: string; status?: string };
        // Only an accepted row is membership: not inactive, archived (left or
        // removed, even before teamsChanged strips the firm) or a pending
        // invite. A row with no status predates the field and stays a member.
        if (
          data.designFirmID &&
          (data.status === TeamsStatus.ACTIVE || data.status == null)
        ) {
          active.add(data.designFirmID);
        }
      });
    } catch (err) {
      console.error("[useWorkspaceContext] fetchActiveFirms failed:", err);
      chunk.forEach((firmId) => failedIds.add(firmId));
    }
  }
  return { ids: active, failedIds };
}

/**
 * Resolves the user's active workspace. Backed by a singleton zustand store
 * (useWorkspaceStore) so every consumer in the app sees the same selection
 * and re-renders when it changes. Persists per-uid in localStorage.
 *
 * Falls back to Personal when:
 *   - No stored selection exists
 *   - The stored team workspace is no longer accessible (user removed)
 *
 * See docs/workspaces-architecture.md for the workspace model.
 */
export const useWorkspaceContext = (
  uid: string | null | undefined,
  permissions: Permissions | null | undefined,
): WorkspaceContext => {
  // Workspaces explicitly disabled (see featureFlag.ts). Force a
  // Personal-only context so every consumer behaves exactly as it did
  // pre-workspaces — no team resolution, no firestore reads, no localStorage
  // reconciliation. `workspacesEnabled()` is stable for the component's
  // lifetime (driven by the build env), so this early return keeps a consistent
  // hook order across renders.
  if (!workspacesEnabled()) {
    const personal: Workspace = uid
      ? { id: PERSONAL_ID(uid), kind: "personal", uid, label: "Personal" }
      : PLACEHOLDER_LOADING_WORKSPACE;
    return {
      active: personal,
      workspaces: uid ? [personal] : [],
      inactiveTeamIds: EMPTY_FIRM_IDS,
      inactiveAdminTeamIds: EMPTY_FIRM_IDS,
      switchWorkspace: NOOP_SWITCH,
      isPersonal: true,
      isTeam: false,
      teamRole: null,
      isAdmin: false,
      isActiveMemberOfActiveWorkspace: true,
      teamStatusResolved: true,
      teamStatusFailedIds: EMPTY_FIRM_IDS,
      loading: !uid,
    };
  }

  const baseWorkspaces = useMemo(
    () => buildWorkspaces(uid, permissions),
    [uid, permissions],
  );

  // Resolve which of the user's permission firms they are CURRENTLY active in.
  // Mirrors the backend's isUserMemberOfTeam check so the switcher doesn't
  // expose teams that would 403 on the next concept generation.
  //
  // An inactive admin leaves the workspace too (the Teams sharing rules refuse
  // their team lists and creates); the switcher offers them Reactivate instead
  // (`inactiveAdminTeamIds`). True removal is archive, which drops the
  // teamsAdmin permission, so the firm leaves baseWorkspaces entirely.
  const teamFirmIds = useMemo(
    () =>
      baseWorkspaces
        .filter((w): w is Extract<Workspace, { kind: "team" }> => w.kind === "team")
        .map((w) => w.firmId),
    [baseWorkspaces],
  );
  const teamFirmIdsKey = teamFirmIds.join(",");

  // Firms the user administers (Reactivate is offered for them).
  const adminFirmIds = useMemo(
    () =>
      new Set(
        baseWorkspaces
          .filter(
            (w): w is Extract<Workspace, { kind: "team" }> =>
              w.kind === "team" && isAdminRole(w.role),
          )
          .map((w) => w.firmId),
      ),
    [baseWorkspaces],
  );

  const [activeFirms, setActiveFirms] = useState<ActiveFirmsEntry | null>(() =>
    uid ? activeFirmsCache.get(uid) ?? null : null,
  );

  // Bumped by refreshActiveFirms() (after a teams.status mutation) to force the
  // fetch effect below to re-run and re-resolve the active-firms set.
  const activeFirmsVersion = useWorkspaceStore((s) => s.activeFirmsVersion);

  // The resolved active-firms set, but ONLY when it was resolved for the
  // user's CURRENT firm set. A value resolved for a different (e.g. smaller,
  // pre-team-creation) firm set is stale — treat it as null ("still
  // resolving") so `workspaces` shows everything and the reconciliation
  // effect doesn't bounce a freshly-added team back to Personal before the
  // refetch for the new firm set lands.
  const resolvedFirmIds =
    activeFirms && activeFirms.key === teamFirmIdsKey ? activeFirms.ids : null;
  // An admin's team whose status read failed stays open, as before: it isn't
  // a known inactive membership, so it must not offer Reactivate.
  const failedFirmIds = resolvedFirmIds ? activeFirms!.failedIds : null;
  const unknownAdminFirm = (firmId: string) =>
    adminFirmIds.has(firmId) && !!failedFirmIds?.has(firmId);

  useEffect(() => {
    if (!uid || teamFirmIds.length === 0) {
      setActiveFirms({
        key: teamFirmIdsKey,
        ids: new Set(),
        failedIds: new Set(),
      });
      return;
    }
    const cached = activeFirmsCache.get(uid);
    if (cached && cached.key === teamFirmIdsKey) {
      setActiveFirms(cached);
      return;
    }
    // No valid cache for the current firm set (cache miss, or the cached
    // value was resolved for a different set — e.g. right after a team was
    // created). `resolvedFirmIds` is already null in this state, so
    // `workspaces` shows everything while the fetch resolves.
    let cancelled = false;
    const flightKey = `${uid}::${teamFirmIdsKey}`;
    const existing = activeFirmsInFlight.get(flightKey);
    const p = existing ?? fetchActiveFirms(uid, teamFirmIds);
    if (!existing) activeFirmsInFlight.set(flightKey, p);
    p.then((result) => {
      const entry: ActiveFirmsEntry = { key: teamFirmIdsKey, ...result };
      activeFirmsCache.set(uid, entry);
      activeFirmsInFlight.delete(flightKey);
      if (!cancelled) setActiveFirms(entry);
    }).catch(() => {
      activeFirmsInFlight.delete(flightKey);
    });
    return () => {
      cancelled = true;
    };
    // activeFirmsVersion is included so an explicit refreshActiveFirms() (which
    // also clears the cache) re-runs this fetch even though uid/firm set are
    // unchanged — e.g. after the user reactivates their own membership.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, teamFirmIdsKey, activeFirmsVersion]);

  const workspaces = useMemo(() => {
    // Until the active-firms fetch resolves, show everything so a returning
    // user with a persisted team selection doesn't flicker into Personal.
    // After resolution, inactive firms are excluded.
    if (resolvedFirmIds === null) return baseWorkspaces;
    return baseWorkspaces.filter(
      (w) =>
        w.kind === "personal" ||
        resolvedFirmIds.has(w.firmId) ||
        unknownAdminFirm(w.firmId),
    );
  }, [baseWorkspaces, resolvedFirmIds, adminFirmIds, failedFirmIds]);

  const inactiveTeamIds = useMemo(() => {
    if (resolvedFirmIds === null) return new Set<string>();
    const inactive = new Set<string>();
    for (const firmId of teamFirmIds) {
      if (!resolvedFirmIds.has(firmId) && !unknownAdminFirm(firmId)) {
        inactive.add(firmId);
      }
    }
    return inactive;
  }, [teamFirmIds, resolvedFirmIds, adminFirmIds, failedFirmIds]);
  const inactiveAdminTeamIds = useMemo(
    () => new Set([...inactiveTeamIds].filter((firmId) => adminFirmIds.has(firmId))),
    [inactiveTeamIds, adminFirmIds],
  );

  const storedActiveId = useWorkspaceStore((s) =>
    uid ? s.activeIdByUid[uid] : undefined,
  );
  const setActive = useWorkspaceStore((s) => s.setActive);
  const hydrate = useWorkspaceStore((s) => s.hydrate);

  // One-time hydration from localStorage per uid. The store guards against
  // re-hydrating if a value is already set.
  useEffect(() => {
    if (!uid) return;
    const stored = readStored(uid);
    if (stored) hydrate(uid, stored);
  }, [uid, hydrate]);

  // Cross-tab sync: when localStorage changes in another tab, mirror it.
  useEffect(() => {
    if (!uid || typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== STORAGE_KEY(uid)) return;
      if (e.newValue) setActive(uid, e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [uid, setActive]);

  // Reconcile when stored value becomes invalid (e.g. user removed from team).
  //
  // Defensive guards (prevent the "team flashes for a second then reverts
  // to Personal" regression):
  //   - Skip while `permissions` is null/undefined. That state is
  //     ambiguous (could be "loading" or "no permissions doc"); bouncing
  //     during the loading case would persist Personal to localStorage
  //     and the user would have to manually switch back.
  //   - If the stored firm IS in `baseWorkspaces` (permissions has it)
  //     but absent from the filtered `workspaces` because the active-firms
  //     set for the current firm set hasn't resolved yet (resolvedFirmIds
  //     === null), wait. Bouncing the moment it starts loading would punt to
  //     Personal even when the team is reachable — including right after a
  //     team is created, when the prior set is stale for the new firm set.
  useEffect(() => {
    if (!uid || !storedActiveId) return;
    if (!permissions) return;
    if (workspaces.some((w) => w.id === storedActiveId)) return;
    if (
      resolvedFirmIds === null &&
      baseWorkspaces.some((w) => w.id === storedActiveId)
    ) {
      return;
    }
    const fallback = PERSONAL_ID(uid);
    setActive(uid, fallback);
    writeStored(uid, fallback);
  }, [
    uid,
    storedActiveId,
    workspaces,
    baseWorkspaces,
    setActive,
    permissions,
    resolvedFirmIds,
  ]);

  const activeId = useMemo<string | null>(() => {
    if (!uid) return null;
    if (storedActiveId && workspaces.some((w) => w.id === storedActiveId)) {
      return storedActiveId;
    }
    // Defensive: while permissions are still loading we don't yet know
    // whether the stored team is reachable. Hold onto the stored value
    // rather than flashing to Personal — the reconciliation effect
    // (gated on `permissions` truthiness) will persist the actual
    // fallback later only if the team is genuinely gone.
    if (!permissions && storedActiveId) return storedActiveId;
    return PERSONAL_ID(uid);
  }, [uid, storedActiveId, workspaces, permissions]);

  const switchWorkspace = useCallback(
    (id: string) => {
      if (!uid) return;
      if (!workspaces.some((w) => w.id === id)) return;
      setActive(uid, id);
      writeStored(uid, id);
    },
    [uid, workspaces, setActive],
  );

  const active = useMemo<Workspace>(() => {
    if (!uid) return PLACEHOLDER_LOADING_WORKSPACE;
    const found = workspaces.find((w) => w.id === activeId);
    if (found) return found;
    // Defensive: when permissions are still loading the team may not be
    // present in `workspaces` yet. Reconstruct a minimal team workspace
    // from the stored activeId so the active doesn't visibly flash to
    // Personal until permissions resolve.
    const firmId = parseFirmIdFromWorkspaceId(activeId);
    if (firmId) {
      return { kind: "team", id: activeId, firmId, role: TeamRole.ADMIN };
    }
    return workspaces[0] ?? PLACEHOLDER_LOADING_WORKSPACE;
  }, [uid, activeId, workspaces]);

  const isPersonal = active.kind === "personal";
  const isTeam = active.kind === "team";
  const teamRole = active.kind === "team" ? active.role : null;
  const isAdmin =
    active.kind === "team" &&
    (active.role === TeamRole.ADMIN ||
      active.role === TeamRole.DESIGNER_AND_ADMIN);

  // Roster-active in the active team? `resolvedFirmIds` is built from
  // fetchActiveFirms, which excludes any firm whose teams-row status is
  // inactive (for ANY role) — so this is the authoritative "active spender"
  // signal. Default true for Personal and while still resolving (null), so we
  // never flash-hide a balance that is actually spendable.
  const isActiveMemberOfActiveWorkspace =
    active.kind !== "team"
      ? true
      : resolvedFirmIds === null
        ? true
        : resolvedFirmIds.has(active.firmId);

  return {
    active,
    workspaces,
    inactiveTeamIds,
    inactiveAdminTeamIds,
    switchWorkspace,
    isPersonal,
    isTeam,
    teamRole,
    isAdmin,
    isActiveMemberOfActiveWorkspace,
    teamStatusResolved: resolvedFirmIds !== null,
    teamStatusFailedIds:
      resolvedFirmIds !== null && activeFirms
        ? activeFirms.failedIds
        : EMPTY_FIRM_IDS,
    loading: !uid,
  };
};

/**
 * Resolve the ACTIVE (non-inactive) subset of a SPECIFIC list of firms for
 * `uid`, independent of the active workspace and of `workspacesEnabled()`.
 *
 * `useWorkspaceContext` only resolves the active-firms set in ENABLED mode (it
 * short-circuits when gated), and its `isActiveMemberOfActiveWorkspace` only
 * answers for the ACTIVE workspace — which is forced to Personal in gated prod.
 * The Pro/credit CAPABILITY firm (`useCreditWorkspace`) can be a different firm
 * in that mode, so its roster status must be resolved directly. Without this an
 * inactive (deactivated) designer whose seat still lingers in `teamsDesigner`
 * keeps the team wallet + team Pro in production.
 *
 * Does its own `teams` read via the shared `fetchActiveFirms`; returns `null`
 * while resolving (callers decide optimism), and an empty set immediately for
 * an empty `firmIds`. Cached per (uid, firmSet) so consumers share one read.
 */
export const useActiveFirmIds = (
  uid: string | null | undefined,
  firmIds: string[],
): Set<string> | null => {
  // Re-resolve when refreshActiveFirms() bumps this (it also clears the cache),
  // e.g. after the user reactivates their own membership.
  const activeFirmsVersion = useWorkspaceStore((s) => s.activeFirmsVersion);
  const firmIdsKey = firmIds.join(",");
  const [resolved, setResolved] = useState<{ key: string; ids: Set<string> } | null>(
    () =>
      uid && activeFirmSetCache.has(activeFirmSetKey(uid, firmIdsKey))
        ? { key: firmIdsKey, ids: activeFirmSetCache.get(activeFirmSetKey(uid, firmIdsKey))! }
        : null,
  );

  useEffect(() => {
    if (!uid || firmIds.length === 0) {
      setResolved({ key: firmIdsKey, ids: new Set() });
      return;
    }
    const key = activeFirmSetKey(uid, firmIdsKey);
    const cached = activeFirmSetCache.get(key);
    if (cached) {
      setResolved({ key: firmIdsKey, ids: cached });
      return;
    }
    let cancelled = false;
    const existing = activeFirmSetInFlight.get(key);
    // Fail-closed: a failed read leaves those firms out of the active set.
    const p = existing ?? fetchActiveFirms(uid, firmIds).then((r) => r.ids);
    if (!existing) activeFirmSetInFlight.set(key, p);
    p.then((ids) => {
      activeFirmSetCache.set(key, ids);
      activeFirmSetInFlight.delete(key);
      if (!cancelled) setResolved({ key: firmIdsKey, ids });
    }).catch(() => {
      activeFirmSetInFlight.delete(key);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, firmIdsKey, activeFirmsVersion]);

  // Guard against a stale value resolved for a DIFFERENT firm set.
  return resolved && resolved.key === firmIdsKey ? resolved.ids : null;
};

/**
 * Is `uid` an ACTIVE (non-inactive) roster member of a SPECIFIC firm? Thin
 * single-firm wrapper over useActiveFirmIds. Optimistic — returns `true` while
 * resolving and when `firmId` is null (personal / no firm) so we never
 * flash-hide a capability that is actually active.
 */
export const useIsActiveRosterMember = (
  uid: string | null | undefined,
  firmId: string | null | undefined,
): boolean => {
  const ids = useActiveFirmIds(uid, firmId ? [firmId] : []);
  if (!firmId) return true; // personal / no firm → not gated by roster
  if (ids === null) return true; // still resolving → optimistic
  return ids.has(firmId);
};

/**
 * Clear the in-memory active-firms cache for a specific uid (or globally
 * if no uid is given). Use this after a server-side change that affects
 * the user's firm memberships — e.g. team workspace creation, accepting
 * an invite — so the next render's useWorkspaceContext refetches.
 */
export const clearActiveFirmsCache = (uid?: string | null) => {
  if (uid) {
    activeFirmsCache.delete(uid);
    // In-flight promises are keyed `${uid}::${teamFirmIdsKey}` — drop every
    // entry for this uid regardless of which firm set it was fetching.
    for (const key of activeFirmsInFlight.keys()) {
      if (key === uid || key.startsWith(`${uid}::`)) {
        activeFirmsInFlight.delete(key);
      }
    }
    // Active-firm-set cache/in-flight are keyed `${uid}::${firmIdsKey}`.
    for (const key of activeFirmSetCache.keys()) {
      if (key.startsWith(`${uid}::`)) activeFirmSetCache.delete(key);
    }
    for (const key of activeFirmSetInFlight.keys()) {
      if (key.startsWith(`${uid}::`)) activeFirmSetInFlight.delete(key);
    }
  } else {
    activeFirmsCache.clear();
    activeFirmsInFlight.clear();
    activeFirmSetCache.clear();
    activeFirmSetInFlight.clear();
  }
};

// Back-compat alias kept for existing tests.
export const __resetActiveFirmsCacheForTests = () => clearActiveFirmsCache();

/**
 * Drop the cached active-firms set AND force every live `useWorkspaceContext`
 * to re-resolve it. Call this after a `teams.status` mutation that changes
 * whether the user is an ACTIVE member — e.g. when a user reactivates their
 * own membership — so the switcher and the credit balance update without a
 * page refresh (the cached set is otherwise non-reactive to status changes).
 */
export const refreshActiveFirms = (uid?: string | null) => {
  clearActiveFirmsCache(uid);
  useWorkspaceStore.getState().bumpActiveFirmsVersion();
};

/**
 * Set AND persist the active workspace for a uid from outside a React hook.
 *
 * `useWorkspaceStore.setActive` only updates the in-memory store; it does NOT
 * write localStorage (the React `switchWorkspace` callback pairs it with
 * `writeStored`). Use this helper from async creation paths — e.g. the
 * create-team wizard — so the new selection both takes effect immediately AND
 * survives a hard reload. Keeps the localStorage key encapsulated here.
 */
export function persistActiveWorkspace(
  uid: string | null | undefined,
  id: string,
): void {
  if (!uid) return;
  useWorkspaceStore.getState().setActive(uid, id);
  writeStored(uid, id);
}

/**
 * Non-React accessor for the active workspace's design firm id.
 *
 * Returns the team's firmId when the active workspace is a team, or null
 * when Personal (or when nothing is stored). Intended for stamping new
 * documents at create time from places that aren't already inside a React
 * hook — Save button handlers, useBoardManagement.handleCreateNewBoard,
 * etc.
 *
 * Reads from the singleton useWorkspaceStore so it always reflects the
 * user's current selection. The store is hydrated lazily by
 * useWorkspaceContext on first mount; if no React hook has hydrated yet
 * (rare edge case), this falls back to localStorage.
 */
export function getActiveDesignFirmId(
  uid: string | null | undefined,
): string | null {
  // Workspaces explicitly disabled: always stamp new docs as Personal
  // (design_firm_id = null), the pre-workspaces value. See featureFlag.ts.
  if (!workspacesEnabled()) return null;
  if (!uid) return null;
  let activeId: string | undefined =
    useWorkspaceStore.getState().activeIdByUid[uid];
  if (!activeId) {
    activeId = readStored(uid) ?? undefined;
  }
  return parseFirmIdFromWorkspaceId(activeId);
}

/**
 * Parse the design firm id out of a stored workspace id. A team workspace is
 * stored as `team:<firmId>`; Personal (or anything else / unset) yields null.
 */
export function parseFirmIdFromWorkspaceId(
  id: string | null | undefined,
): string | null {
  return id && id.startsWith("team:") ? id.slice("team:".length) : null;
}

/**
 * Does a workspace-stamped document belong to the uid's currently-active
 * workspace? Shared by the list hooks (boards/collections/projects) so the
 * in-hook filter predicate lives in exactly one place.
 *
 * Resolves the active firm via `getActiveDesignFirmId` — the SAME store +
 * localStorage-fallback resolution the list hooks use to BUILD their query.
 * The two must agree: if the filter resolved store-only while the query
 * resolved with the localStorage fallback, then on a hard refresh — before
 * the zustand store has hydrated from localStorage — a team workspace's query
 * would fetch the team-stamped docs but this filter would resolve the active
 * firm to `null` and drop every one of them, so freshly-created projects/
 * boards/collections "disappear on refresh" until the store hydrates. Reading
 * through the same accessor keeps the query and the filter consistent in that
 * window. Pre-migration docs with no `design_firm_id` are treated as Personal.
 */
export function docBelongsToActiveWorkspace(
  doc: { design_firm_id?: string | null },
  userId: string | null,
): boolean {
  if (!userId) return true; // No uid yet — don't filter.
  const activeFirmId = getActiveDesignFirmId(userId);
  return (doc.design_firm_id ?? null) === activeFirmId;
}
